import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from './components/ui/sonner';
import { LoadingState } from './components/common/EmptyState';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import AuthCallback from './pages/auth/AuthCallback';

// Layout
import Layout from './components/layout/Layout';

// Propietario pages
import PropietarioUnidades from './pages/propietario/PropietarioUnidades';
import PropietarioRenta from './pages/propietario/PropietarioRenta';
import PropietarioExpensas from './pages/propietario/PropietarioExpensas';
import PropietarioUnidadDetalle from './pages/propietario/PropietarioUnidadDetalle';
import PropietarioNovedades from './pages/propietario/PropietarioNovedades';
import PropietarioMisDatos from './pages/propietario/PropietarioMisDatos';
import PropietarioFacturacion from './pages/propietario/PropietarioFacturacion';
import PropietarioReclamos from './pages/propietario/PropietarioReclamos';
import PropietarioCowork from './pages/propietario/PropietarioCowork';

// Analista pages
import AnalistaEdificios from './pages/analista/AnalistaEdificios';
import AnalistaEdificioDetalle from './pages/analista/AnalistaEdificioDetalle';
import AnalistaPropietarios from './pages/analista/AnalistaPropietarios';
import AnalistaClientes from './pages/analista/AnalistaClientes';
import AnalistaGlobalCitizen from './pages/analista/AnalistaGlobalCitizen';
import AnalistaCotizaciones from './pages/analista/AnalistaCotizaciones';
import AnalistaFacturasPendientes from './pages/analista/AnalistaFacturasPendientes';
// AnalistaBackup deprecated — moved to /admin/backups (admin only)
import AnalistaComisionesPage from './pages/analista/AnalistaComisionesPage';
import AnalistaReporteComisionesPage from './pages/analista/AnalistaReporteComisionesPage';
import AFIPConfigPage from './pages/analista/AFIPConfigPage';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminReporteEdificios from './pages/admin/AdminReporteEdificios';
import AdminReporteUnidades from './pages/admin/AdminReporteUnidades';
import AdminUsuarios from './pages/admin/AdminUsuarios';
import AdminBackups from './pages/admin/AdminBackups';

// Mi Cuenta
import MiCuentaPage from './pages/mi-cuenta/MiCuentaPage';

import DocumentosPage from './pages/DocumentosPage';
import ComercialLeads from './pages/comercial/ComercialLeads';
import ComercialBaseDatos from './pages/comercial/ComercialBaseDatos';
import CoworkForesta from './pages/analista/CoworkForesta';
import RevisorEdificios from './pages/revisor/RevisorEdificios';
import RevisorEdificioDetalle from './pages/revisor/RevisorEdificioDetalle';
import RevisorNovedades from './pages/revisor/RevisorNovedades';
import RevisorReclamos from './pages/revisor/RevisorReclamos';

import ExpensasEdificios from './pages/expensas/ExpensasEdificios';
import ExpensasEdificioDetalle from './pages/expensas/ExpensasEdificioDetalle';
import ExpensasPeriodoDetalle from './pages/expensas/ExpensasPeriodoDetalle';
import MisExpensas from './pages/expensas/MisExpensas';
import InquilinoDashboard from './pages/inquilino/InquilinoDashboard';
import InquilinoExpensas from './pages/inquilino/InquilinoExpensas';
import InquilinoUnidadDetalle from './pages/inquilino/InquilinoUnidadDetalle';

import OperacionesEdificios from './pages/operaciones/OperacionesEdificios';
import OperacionesEdificioDetalle from './pages/operaciones/OperacionesEdificioDetalle';
import OperacionesIncidenciasKanban from './pages/operaciones/OperacionesIncidenciasKanban';
import OperacionesReservas from './pages/operaciones/OperacionesReservas';
import OperacionesConfigurar from './pages/operaciones/OperacionesConfigurar';
import { EdificioOpProvider } from './contexts/EdificioOpContext';

import EdificioReservas from './pages/edificio/EdificioReservas';
import EdificioMiUnidad from './pages/edificio/EdificioMiUnidad';
import EdificioIncidencias from './pages/edificio/EdificioIncidencias';

import ActivarPropietarioPage from './pages/activacion/ActivarPropietarioPage';
import ActivarInquilinoPage from './pages/activacion/ActivarInquilinoPage';

import './App.css';

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingState message="Verificando sesión..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles) {
    const userRoles = [user?.rol, ...(user?.roles_extra || [])].filter(Boolean);
    const allowed = userRoles.some(r => allowedRoles.includes(r));
    if (!allowed) {
      // Redirect to appropriate dashboard based on role
      if (user?.rol === 'propietario') {
      return <Navigate to="/propietario" replace />;
    } else if (user?.rol === 'analista') {
      return <Navigate to="/analista/edificios" replace />;
    } else if (user?.rol === 'comercial') {
      return <Navigate to="/comercial/leads" replace />;
    } else if (user?.rol === 'admin') {
      return <Navigate to="/admin/edificios" replace />;
    } else if (user?.rol === 'revisor') {
      return <Navigate to="/revisor/edificios" replace />;
    } else if (user?.rol === 'administrador_consorcio') {
      return <Navigate to="/expensas" replace />;
    } else if (user?.rol === 'inquilino') {
      return <Navigate to="/inquilino" replace />;
    } else if (user?.rol === 'operaciones') {
      return <Navigate to="/operaciones/reservas" replace />;
    } else if (user?.rol === 'usuario_edificio') {
      return <Navigate to="/edificio/reservas" replace />;
    }
    return <Navigate to="/login" replace />;
    }
  }

  return children;
};

// Home redirect based on role
const HomeRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingState message="Cargando..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.rol === 'propietario') {
    return <Navigate to="/propietario" replace />;
  } else if (user?.rol === 'analista') {
    return <Navigate to="/analista/edificios" replace />;
  } else if (user?.rol === 'comercial') {
    return <Navigate to="/comercial/leads" replace />;
  } else if (user?.rol === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  } else if (user?.rol === 'revisor') {
    return <Navigate to="/revisor/edificios" replace />;
  } else if (user?.rol === 'administrador_consorcio') {
    return <Navigate to="/consorcio/edificios" replace />;
  } else if (user?.rol === 'inquilino') {
    return <Navigate to="/inquilino" replace />;
  } else if (user?.rol === 'operaciones') {
    return <Navigate to="/operaciones/reservas" replace />;
  } else if (user?.rol === 'usuario_edificio') {
    return <Navigate to="/edificio/reservas" replace />;
  }

  return <Navigate to="/login" replace />;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL fragment (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/activar-propietario" element={<ActivarPropietarioPage />} />
      <Route path="/activar-inquilino" element={<ActivarInquilinoPage />} />

      {/* Home redirect */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Propietario routes */}
      <Route
        path="/propietario"
        element={
          <ProtectedRoute allowedRoles={['propietario']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/propietario/renta" replace />} />
        <Route path="novedades" element={<PropietarioNovedades />} />
        <Route path="renta" element={<PropietarioRenta />} />
        <Route path="renta/:unidadId" element={<PropietarioUnidadDetalle mode="renta" />} />
        <Route path="expensas" element={<PropietarioExpensas />} />
        <Route path="expensas/:unidadId" element={<PropietarioUnidadDetalle mode="expensas" />} />
        <Route path="mis-datos" element={<PropietarioMisDatos />} />
        <Route path="cowork" element={<PropietarioCowork />} />
        <Route path="facturacion" element={<Navigate to="/propietario/renta" replace />} />
        <Route path="reclamos" element={<PropietarioReclamos />} />
        <Route path="unidad/:unidadId" element={<PropietarioUnidadDetalle mode="renta" />} />
      </Route>

      {/* Analista routes */}
      <Route
        path="/analista"
        element={
          <ProtectedRoute allowedRoles={['analista']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/analista/edificios" replace />} />
        <Route path="global-citizen" element={<AnalistaGlobalCitizen />} />
        <Route path="edificios" element={<AnalistaEdificios />} />
        <Route path="edificio/:edificioId" element={<AnalistaEdificioDetalle />} />
        <Route path="comisiones" element={<AnalistaComisionesPage />} />
        <Route path="propietarios" element={<AnalistaPropietarios />} />
        <Route path="clientes" element={<AnalistaClientes />} />
        <Route path="cotizaciones" element={<AnalistaCotizaciones />} />
        <Route path="facturas-pendientes" element={<AnalistaFacturasPendientes />} />
        <Route path="backup" element={<Navigate to="/admin/backups" replace />} />
        <Route path="afip" element={<AFIPConfigPage />} />
        <Route path="documentos" element={<DocumentosPage />} />
      </Route>

      {/* Comercial routes (reutilizan componentes de analista) */}
      <Route
        path="/comercial"
        element={
          <ProtectedRoute allowedRoles={['comercial']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/comercial/leads" replace />} />
        <Route path="leads" element={<ComercialLeads />} />
        <Route path="base-datos" element={<ComercialBaseDatos />} />
        <Route path="edificios" element={<AnalistaEdificios />} />
        <Route path="edificio/:edificioId" element={<AnalistaEdificioDetalle />} />
        <Route path="propietarios" element={<AnalistaPropietarios />} />
        <Route path="comisiones" element={<AnalistaComisionesPage />} />
        <Route path="reporte" element={<AnalistaReporteComisionesPage />} />
        <Route path="documentos" element={<DocumentosPage />} />
      </Route>

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="reclamos" element={<RevisorReclamos />} />
        <Route path="edificios" element={<AdminReporteEdificios />} />
        <Route path="unidades" element={<AdminReporteUnidades />} />
        <Route path="usuarios" element={<AdminUsuarios />} />
        <Route path="backups" element={<AdminBackups />} />
        <Route path="documentos" element={<DocumentosPage />} />
      </Route>

      {/* Revisor routes */}
      <Route
        path="/revisor"
        element={
          <ProtectedRoute allowedRoles={['revisor']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/revisor/edificios" replace />} />
        <Route path="edificios" element={<RevisorEdificios />} />
        <Route path="novedades" element={<RevisorNovedades />} />
        <Route path="reclamos" element={<RevisorReclamos />} />
        <Route path="edificio/:edificioId" element={<RevisorEdificioDetalle />} />
        <Route path="documentos" element={<DocumentosPage />} />
      </Route>

      {/* Administrador Consorcio routes */}
      <Route
        path="/consorcio"
        element={
          <ProtectedRoute allowedRoles={['administrador_consorcio', 'admin']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/expensas" replace />} />
        <Route path="edificios" element={<Navigate to="/expensas" replace />} />
        <Route path="documentos" element={<DocumentosPage />} />
      </Route>

      {/* Expensas (módulo dedicado) */}
      <Route
        path="/expensas"
        element={
          <ProtectedRoute allowedRoles={['administrador_consorcio', 'admin']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ExpensasEdificios />} />
        <Route path=":edificioId" element={<ExpensasEdificioDetalle />} />
      </Route>

      {/* Inquilino routes */}
      <Route
        path="/inquilino"
        element={
          <ProtectedRoute allowedRoles={['inquilino']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/inquilino/expensas" replace />} />
        <Route path="novedades" element={<PropietarioNovedades />} />
        <Route path="expensas" element={<InquilinoExpensas />} />
        <Route path="expensas/:unidadId" element={<InquilinoUnidadDetalle />} />
      </Route>

      {/* Operaciones routes */}
      <Route
        path="/operaciones"
        element={
          <ProtectedRoute allowedRoles={['operaciones', 'admin']}>
            <EdificioOpProvider>
              <Layout />
            </EdificioOpProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/operaciones/reservas" replace />} />
        <Route path="reservas" element={<OperacionesReservas />} />
        <Route path="reclamos" element={<OperacionesIncidenciasKanban />} />
        <Route path="configurar" element={<OperacionesConfigurar />} />
        <Route path="edificios-grid" element={<OperacionesEdificios />} />
        <Route path="edificio/:edificioId" element={<OperacionesEdificioDetalle />} />
        <Route path="incidencias" element={<Navigate to="/operaciones/reclamos" replace />} />
      </Route>

      {/* Usuario edificio routes */}
      <Route
        path="/edificio"
        element={
          <ProtectedRoute allowedRoles={['usuario_edificio']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/edificio/reservas" replace />} />
        <Route path="reservas" element={<EdificioReservas />} />
        <Route path="incidencias" element={<EdificioIncidencias />} />
        <Route path="mi-unidad" element={<EdificioMiUnidad />} />
      </Route>

      {/* Mi cuenta (todos los autenticados) */}
      <Route
        path="/mi-cuenta"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<MiCuentaPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
